import { useMemo } from 'react';
import flow from 'lodash/fp/flow';
import type { ColDef } from 'ag-grid-community';

import {
  addHeaderTooltipPropsForColDefs,
  GRID_FILTER_BUTTONS,
  GRID_SERVICE_KEYS,
  setExelModeFilter,
} from '@/components/AgGrid/helpers';
import { addFilters } from '@/components/AgGrid/helpers/filterHelpers';
import { addTooltipPropsForColDefs } from '@/components/AgGrid/helpers/tooltipColDef';
import { addColumnsVisibilityColumnDef } from '@/components/AgGrid/Documents/serviceColumnDefs';
import { filterMapping } from '@/components/AgGrid/BankFeedTransactions/filterHelpers';
import {
  applyColumnOverrides,
  BANK_FEED_COLUMN_OVERRIDES,
} from '@/components/AgGrid/BankFeedTransactions/columnWidths';
import useAgGridRtlMethods from '@/components/AgGrid/hooks/useAgGridRtlMethods';

import useFetchColumnDefs from '@/pages/company/BankFeedRoute/components/BankFeedGrid/useFetchColumnDefs';
import type { SetColumnFilterParams } from '@/pages/company/BankFeedRoute/components/BankFeedGrid/BankFeedSetColumnFilter';
import type { BankFeedAccountType } from '@/domain/bankFeed/schemas';

// Filter type token registered in `gridComponents.ts`; the set-filter columns are
// the ones that receive the injected `loadOptions` via `filterParams`.
const SET_FILTER_COMPONENT = 'bankFeedSetColumnFilter';

// `defaultColDef.suppressHeaderMenuButton: true` (in `useAgGridProps`) suppresses the header
// menu globally — AG Grid only passes `enableMenu=true` to the custom header when the column
// itself opts back in. The columns-visibility service column needs that pass-through so the
// custom header actually renders the visibility-menu button.
const enableMenuOnVisibilityColumn = (columns: ColDef[]): ColDef[] =>
  columns.map((col) =>
    col.field === GRID_SERVICE_KEYS.COLUMNS_VISIBILITY_MENU
      ? { ...col, suppressHeaderMenuButton: false }
      : col,
  );

type UseColumnDefsArgs = {
  accountType: BankFeedAccountType;
  loadOptions: SetColumnFilterParams['loadOptions'];
};

type UseColumnDefsResult = {
  columnDefs: ColDef[];
  isFetched: boolean;
};

// Adapter pipeline: add tooltipField for text-bearing types rendered by AG Grid defaults
// (truncation detection works natively there), translate BE filter operators, apply Windows
// excel-mode filter chrome, opt headers into truncation tooltips (must run AFTER addFilters —
// it rewrites headerComponentParams), append the right-pinned columns-visibility service
// column, re-enable its menu, then flip pinned side in RTL.
const useColumnDefs = ({ accountType, loadOptions }: UseColumnDefsArgs): UseColumnDefsResult => {
  const { rawColumnDefs, rawColumnDefsIsFetched } = useFetchColumnDefs({ accountType });
  const { invertPinnedWhenRTL } = useAgGridRtlMethods();

  const columnDefs = useMemo<ColDef[]>(() => {
    const base = flow([
      applyColumnOverrides(BANK_FEED_COLUMN_OVERRIDES),
      addTooltipPropsForColDefs(['string', 'date', 'select']),
      addFilters(filterMapping),
      setExelModeFilter('windows', [GRID_FILTER_BUTTONS.APPLY, GRID_FILTER_BUTTONS.RESET]),
      addHeaderTooltipPropsForColDefs,
      addColumnsVisibilityColumnDef,
      enableMenuOnVisibilityColumn,
      invertPinnedWhenRTL,
    ])(rawColumnDefs);

    // Inject the set-filter data loader into the columns that use it. `loadOptions`
    // is stable (memoised in BankFeedGrid), so this map does not change `columnDefs`
    // identity on currency change — no re-render / refetch storm across columns.
    return base.map((col: ColDef) =>
      col.filter === SET_FILTER_COMPONENT
        ? { ...col, filterParams: { ...col.filterParams, loadOptions } }
        : col,
    );
  }, [rawColumnDefs, invertPinnedWhenRTL, loadOptions]);

  return { columnDefs, isFetched: rawColumnDefsIsFetched };
};

export default useColumnDefs;
